<template>
    <div class="container-month-year" ref="container" :class="{focus: isShow}">
        <input :class="input_class"
               :id="input_id"
               :disabled="disabled"
               type="text"
               autocomplete="off"
               placeholder="MM/AAAA"
               v-closable="{
                exclude: ['container'],
                handler: 'hide'
               }"
               v-mask="'##/####'"
               ref="input"
               :value="value"
               @focus="focus"
               @keyup="keyup"
               @keyup.esc.prevent.stop="hide"
               @keydown="keydown">
        <transition name="dropdown" mode="out-in">
            <div v-show="isShow" class="dropdown" ref="dropdown">
                <ul class="month">
                    <li v-for="m in months"
                        :class="{active: m.key === selectedMonth}"
                        @click="selectMonth(m)"
                        :key="m.key">{{m.label}}</li>
                </ul>
                <div class="year">
                    <span @click="navigateYear">
                        <svg-icon icon="sort-up"/>
                    </span>
                    <ul>
                        <li v-for="y in years"
                            :class="{active: selectedYear === y}"
                            :key="y"
                            @click="selectYear(y)">{{y}}</li>
                    </ul>
                    <span @click="navigateYear(false)">
                        <svg-icon icon="sort-down"/>
                    </span>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    import dayjs from 'dayjs'

    export default {
        name: "MonthYearInput",
        props: {
            value: {
                type: String,
                default: () => dayjs().format('MM/YYYY')
            },
            input_class: {
                type: [String, Array, Object],
                default: 'form-control plch-grey form-control-sm input plch-c-w t-c'
            },
            input_id: {
                type: String
            },
            disabled: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                isShow: false,
                selectedMonth: new Date().getMonth(),
                selectedYear: new Date().getFullYear(),
                years: []
            }
        },
        methods: {
            keyup(e) {
                if (!/\d/.test(e.key)) return;
                const val = (e.target.value || '').trim();
                this.setValue(val);
            },
            setValue(val) {
                let tokens = null;

                if (/\//.test(val) && val.trim().length === 7) {
                    tokens = val.split('/');

                    try {
                        const month = parseInt(tokens[0]);

                        if (month >= 1 && month <= 12)
                            this.selectedMonth = month - 1;
                    } catch (e) {}

                    try {
                        const year = parseInt(tokens[1]);
                        if (year > 2015) {
                            this.selectedYear = year;
                            this.createYears();
                        }
                    } catch (e) {}

                    this.update();
                }
            },
            keydown(e) {
                if (e.key === 'Tab') this.hide();
                else if (!e.shiftKey) {
                    let pos = 0;
                    switch (e.key) {
                        case 'ArrowUp':
                            pos = this.selectedMonth - 3;
                            if (pos > 11) this.selectedMonth = pos % 3;
                            else this.selectedMonth = pos;
                            break;
                        case 'ArrowDown':
                            pos = this.selectedMonth + 3;
                            if (pos > 11) this.selectedMonth = pos % 3;
                            else this.selectedMonth = pos;
                            break;
                        case 'ArrowRight':
                            pos = this.selectedMonth + 1;
                            if (pos > 11) this.selectedMonth = 0;
                            else this.selectedMonth = pos;
                            break;
                        case 'ArrowLeft':
                            pos = this.selectedMonth - 1;
                            if (pos < 0) this.selectedMonth = 11;
                            else this.selectedMonth = pos;
                            break;
                    }

                    if (/(ArrowUp|ArrowDown|ArrowLeft|ArrowRight)/.test(e.key)) {
                        this.update();
                    }
                } else {
                    let pos = 0;
                    switch (e.key) {
                        case 'ArrowUp':
                            pos = this.selectedYear - 1;
                            break;
                        case 'ArrowDown':
                            pos = this.selectedYear + 1;
                            break;
                    }

                    if (/(ArrowUp|ArrowDown)/.test(e.key)) {
                        this.selectedYear = pos;
                        if (this.years.indexOf(pos) === -1) this.createYears();
                        this.update();
                    }
                }

            },
            update() {
                let date = dayjs();
                date = date.day(1).month(this.selectedMonth).year(this.selectedYear);
                this.$emit('input', date.format('MM/YYYY'));
            },
            focus(e) {
                this.createYears();
                this.$refs.dropdown.style.width = `${e.target.offsetWidth}px`;
                this.isShow = true;
            },
            hide() {
                this.isShow = false;
            },
            createYears() {
                const value = this.$refs.input.value;
                let currentYear = new Date().getFullYear();

                if (/\//.test(value)) {
                    const tokens = value.split('/');
                    currentYear = parseInt(tokens[1]);
                }

                let startYear = currentYear - 2;
                this.years = [];

                while (this.years.length < 5) this.years.push(startYear++);
            },
            navigateYear(isUp = true) {
                if (isUp) {
                    this.years = this.years.filter(y => y !== this.years[this.years.length - 1]);
                    this.years = [this.years[0] - 1, ...this.years];
                } else {
                    this.years = [...this.years, this.years[this.years.length - 1] + 1];
                    this.years = this.years.filter(y => y !== this.years[0]);
                }

            },
            selectMonth(m) {
                this.selectedMonth = m.key;
                this.update();
            },
            selectYear(y) {
                this.selectedYear = y;
                this.update();
            }
        },
        mounted() {
            this.$refs.dropdown.style.width = `${this.$refs.input.offsetWidth}px`;
            this.$refs.dropdown.style.top = `${this.$refs.input.offsetHeight - 1}px`;
        },
        computed: {
            months() {
                return [
                    {key: 0, label: 'Jan'},
                    {key: 1, label: 'Fev'},
                    {key: 2, label: 'Mar'},
                    {key: 3, label: 'Abr'},
                    {key: 4, label: 'Mai'},
                    {key: 5, label: 'Jun'},
                    {key: 6, label: 'Jul'},
                    {key: 7, label: 'Ago'},
                    {key: 8, label: 'Set'},
                    {key: 9, label: 'Out'},
                    {key: 10, label: 'Nov'},
                    {key: 11, label: 'Dez'}
                ];
            }
        },
        watch: {
            isShow(newVal) {
                if (newVal) this.setValue(this.value);
            }
        }
    }
</script>

<style scoped lang="scss">
    @import "../../assets/css/variables";
    .container-month-year {
        position: relative;
        font-size: .8em;
        user-select: none;

        &.focus .input {
            color: #495057;
            background-color: #fff;
            border-color: $theme_color_one;
            outline: 0;
            box-shadow: 0 0 0 0.2rem rgba(darken($theme_color_one, 10), .25);

            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }
    }

    .dropdown {
        position: absolute;
        display: grid;
        grid-template-columns: 2fr 50px;
        z-index: 10;
        background-color: #fff;
        border-left: 1px solid $theme_color_one;
        border-right: 1px solid $theme_color_one;
        border-bottom: 1px solid $theme_color_one;
        border-bottom-right-radius: .2rem;
        border-bottom-left-radius: .2rem;
        box-shadow: 0 15px 25px rgba(0, 0, 0, .6);

        ul.month {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            grid-gap: 5px;
            list-style: none;
            margin: 0;
            padding-left: 5px;
            padding-top: 5px;
            padding-bottom: 5px;

            li {
                user-select: none;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 4px;
                background-color: $theme_color_eleven;
                text-align: center;
                transition: color .3s ease, background-color .3s ease;
                cursor: pointer;

                &:hover, &.active {
                    background-color: $theme_color_one;
                    color: #fff;
                }
                &.active {
                    font-weight: bold;
                }
            }
        }
        .year {
            display: grid;
            grid-template-rows: 1.2em auto 1.2em;
            align-items: stretch;
            justify-items: stretch;
            padding: 5px;

            ul {
                display: grid;
                grid-template-rows: 1fr;
                grid-gap: 2px;
                list-style: none;
                margin: 0;
                padding: 0;
                text-align: center;

                li {
                    user-select: none;
                    cursor: pointer;
                    text-align: center;
                    background-color: $theme_color_eleven;
                    transition: color .3s ease, background-color .3s ease;

                    &:hover, &.active {
                        background-color: $theme_color_one;
                        color: #fff;
                    }

                    &.active {
                        font-weight: bold;
                    }

                    &:first-child {
                        margin-top: 1px;
                    }

                    &:last-child {
                        margin-bottom: 1px;
                    }
                }
            }

            span {
                background-color: $theme_color_one;
                color: #fff;
                text-align: center;
                transition: color .3s ease, background-color .3s ease;
                cursor: pointer;

                &:hover {
                    background-color: $theme_color_three;
                    color: #fff;
                }

                &:first-child {
                    border-top-left-radius: 4px;
                    border-top-right-radius: 4px;
                }

                &:last-child {
                    border-bottom-left-radius: 4px;
                    border-bottom-right-radius: 4px;

                    svg {
                        margin-top: -12px;
                    }
                }

                svg {
                    width: 1em;
                    height: 1em;
                }
            }
        }
    }

    .dropdown-enter-active, .dropdown-leave-active {
        transition: all .3s ease;
    }
    .dropdown-enter, .dropdown-leave-to {
        transform: translateY(-10px);
        opacity: 0;
    }

</style>
